import { useCallback, type KeyboardEvent } from "react";

/**
 * Submit-from-anywhere for dialog forms.
 *
 * The pattern: `<Dialog as="form" onSubmit={…}>` with the primary footer
 * `<Button type="submit">`. A `TextInput` inside the form submits on Enter
 * natively. A textarea needs Enter for new lines, so it submits on
 * Cmd/Ctrl+Enter through the handler this hook returns. Attach it to the
 * textarea's `onKeyDown`; it calls `form.requestSubmit()`, which runs the
 * form's `onSubmit` the same way a submit button does.
 *
 * Without a form ancestor, `onSubmit` is called directly, so the hook also
 * serves a plain `Dialog` whose owner submits from state.
 */
export function useSubmitShortcut(
  onSubmit?: () => void,
): (event: KeyboardEvent<HTMLElement>) => void {
  return useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) return;
      if (event.isDefaultPrevented() || event.nativeEvent.isComposing) return;
      event.preventDefault();
      const form = event.currentTarget.closest("form");
      if (form !== null) {
        form.requestSubmit();
        return;
      }
      onSubmit?.();
    },
    [onSubmit],
  );
}
